import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { Type, Link, AlertCircle, Check, MousePointer, X, SquareMousePointer, Loader2, Save, Share2, RotateCcw, Facebook, Instagram, Twitter, Linkedin, Youtube, Plus, Scan } from 'lucide-react';
import { injectInteractiveAttributes, type TextBlock, type Button as ParsedButton } from '@/lib/email/canva-html-parser';
import { generateCanvaTemplate, type TextBlockMappingType, type ButtonMappingType, type CanvaTemplateMappings } from '@/lib/email/canva-template-generator';
import { detectSocialLinks, mergeWithBusinessLinks, type SocialMediaLinks, type DetectedSocialLink, SOCIAL_PLATFORM_NAMES, getEmptySocialLinks } from '@/lib/email/canva-social-detector';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/hooks/ui/use-toast';

interface EditingTemplate {
  id: string;
  name: string;
  configuration: any;
}

interface CanvaTemplateMappingDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  textBlocks: TextBlock[];
  buttons: ParsedButton[];
  htmlPreview: string;
  businessId: string;
  onSaved: () => void; // Called after successful save
  onCancel: () => void;
  editingTemplate?: EditingTemplate | null; // Template being edited
  /** When true, renders as full page instead of dialog */
  inline?: boolean;
}

interface ReviewForm {
  id: string;
  title: string;
}

interface CouponOffer {
  id: string;
  name: string;
}

interface LoyaltyProgram {
  id: string;
  name: string;
  template_code?: string;
}

type SelectionMode = 'none' | 'main_message' | 'header_text' | 'unsubscribe' | 'button' | 'social_facebook' | 'social_instagram' | 'social_twitter' | 'social_linkedin' | 'social_youtube';

// Map selection modes to social platform keys
const SOCIAL_SELECTION_MAP: Record<string, keyof SocialMediaLinks> = {
  'social_facebook': 'facebook_url',
  'social_instagram': 'instagram_url',
  'social_twitter': 'twitter_url',
  'social_linkedin': 'linkedin_url',
  'social_youtube': 'youtube_url',
};

export function CanvaTemplateMappingDialog({
  open,
  onOpenChange,
  textBlocks,
  buttons,
  htmlPreview,
  businessId,
  onSaved,
  onCancel,
  editingTemplate,
  inline = false
}: CanvaTemplateMappingDialogProps) {
  const { user } = useAuth();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Template name
  const [templateName, setTemplateName] = useState('');

  // Selection mode state
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('none');

  // Highlighted button (for hover preview)
  const [highlightedButtonId, setHighlightedButtonId] = useState<string | null>(null);

  // Hovered element ID (for hover preview in settings panel)
  const [hoveredElementId, setHoveredElementId] = useState<string | null>(null);

  // Text block mappings
  const [textMappings, setTextMappings] = useState<Record<string, TextBlockMappingType>>({});

  // Button mappings
  const [buttonMappings, setButtonMappings] = useState<Record<string, {
    type: ButtonMappingType;
    targetId?: string;
    customUrl?: string;
  }>>({});

  // Data for button type dropdowns
  const [reviewForms, setReviewForms] = useState<ReviewForm[]>([]);
  const [couponOffers, setCouponOffers] = useState<CouponOffer[]>([]);
  const [loyaltyPrograms, setLoyaltyPrograms] = useState<LoyaltyProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Social media links state
  const [businessSocialLinks, setBusinessSocialLinks] = useState<SocialMediaLinks>(getEmptySocialLinks());
  const [templateSocialLinks, setTemplateSocialLinks] = useState<SocialMediaLinks>(getEmptySocialLinks());
  const [detectedSocialLinks, setDetectedSocialLinks] = useState<DetectedSocialLink[]>([]);
  const [manuallyAddedPlatforms, setManuallyAddedPlatforms] = useState<Set<keyof SocialMediaLinks>>(new Set());
  // Manual social link text mappings: platform -> textBlockId
  const [socialTextMappings, setSocialTextMappings] = useState<Record<keyof SocialMediaLinks, string | null>>({
    facebook_url: null,
    instagram_url: null,
    twitter_url: null,
    linkedin_url: null,
    youtube_url: null,
  });

  // Pre-process HTML with interactive attributes and remove width constraints
  const interactiveHtml = useMemo(() => {
    if (!htmlPreview || textBlocks.length === 0 && buttons.length === 0) {
      return htmlPreview;
    }
    let html = injectInteractiveAttributes(htmlPreview, textBlocks, buttons);

    // Remove max-width constraints to fill the preview area
    html = html.replace(/max-width\s*:\s*\d+\s*(px|em|rem|%)?/gi, 'max-width:100%');
    html = html.replace(/width\s*:\s*\d+\s*(px|em|rem)(?=[;"])/gi, 'width:100%');

    // Add margin/padding reset
    const styleReset = '<style>html,body{margin:0!important;padding:0!important;}</style>';
    if (html.includes('</head>')) {
      html = html.replace('</head>', `${styleReset}</head>`);
    } else if (html.includes('<body')) {
      html = html.replace('<body', `${styleReset}<body`);
    } else {
      html = styleReset + html;
    }

    return html;
  }, [htmlPreview, textBlocks, buttons]);

  // Initialize or restore mappings when textBlocks change
  useEffect(() => {
    if (textBlocks.length === 0) return;

    const config = editingTemplate?.configuration;
    const restoredTextMappings: Record<string, TextBlockMappingType> = {};

    textBlocks.forEach(block => {
      // When editing, try to restore the saved mapping
      if (editingTemplate && config?.canvaBlocks) {
        // Match by id or by content similarity
        const savedBlock = config.canvaBlocks.find((b: any) =>
          b.id === block.id || b.originalContent === block.content
        );
        if (savedBlock?.type === 'editable_text' && savedBlock.label === 'Main Message') {
          restoredTextMappings[block.id] = 'main_message';
        } else if (savedBlock?.type === 'editable_text' && savedBlock.label === 'Header Text') {
          restoredTextMappings[block.id] = 'header_text';
        } else if (savedBlock?.type === 'unsubscribe' || (savedBlock?.type === 'editable_text' && savedBlock.label === 'Unsubscribe Link')) {
          restoredTextMappings[block.id] = 'unsubscribe';
        } else {
          restoredTextMappings[block.id] = 'none';
        }
      } else {
        // Default for new templates
        restoredTextMappings[block.id] = 'none';
      }
    });

    setTextMappings(restoredTextMappings);
  }, [textBlocks, editingTemplate]);

  // Initialize button mappings when buttons change
  useEffect(() => {
    const initial: Record<string, { type: ButtonMappingType; targetId?: string; customUrl?: string }> = {};
    buttons.forEach(button => {
      initial[button.id] = { type: 'keep_original' };
    });
    setButtonMappings(initial);
  }, [buttons]);

  // Pre-populate template name when editing
  useEffect(() => {
    if (open && editingTemplate) {
      setTemplateName(editingTemplate.name);
    }
  }, [open, editingTemplate]);

  // Fetch review forms, coupon offers, and loyalty programs
  useEffect(() => {
    if (businessId && open) {
      fetchData();
    }
  }, [businessId, open]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: reviewData } = await supabase
        .from('review_forms')
        .select('id, title')
        .eq('business_id', businessId)
        .eq('active_status', true);
      setReviewForms(reviewData || []);

      const { data: couponData } = await supabase
        .from('coupon_offers')
        .select('id, name')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .is('deleted_at', null);
      setCouponOffers(couponData || []);

      const { data: loyaltyData } = await supabase
        .from('loyalty_programs')
        .select('id, name, template_code')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .is('deleted_at', null);
      setLoyaltyPrograms(loyaltyData || []);

      // Fetch business social links
      const { data: businessData } = await supabase
        .from('businesses')
        .select('facebook_url, instagram_url, twitter_url, linkedin_url, youtube_url')
        .eq('id', businessId)
        .single();

      if (businessData) {
        const bizLinks: SocialMediaLinks = {
          facebook_url: businessData.facebook_url || null,
          instagram_url: businessData.instagram_url || null,
          twitter_url: businessData.twitter_url || null,
          linkedin_url: businessData.linkedin_url || null,
          youtube_url: businessData.youtube_url || null,
        };
        setBusinessSocialLinks(bizLinks);

        // Detect social links in HTML and merge with business profile
        const detected = detectSocialLinks(htmlPreview);
        setDetectedSocialLinks(detected);

        // Auto-populate template social links from detected + business profile
        const merged = mergeWithBusinessLinks(detected, bizLinks);

        // If editing, restore saved social links, otherwise use merged
        if (editingTemplate?.configuration?.socialLinks) {
          const savedLinks = editingTemplate.configuration.socialLinks as SocialMediaLinks;
          setTemplateSocialLinks(savedLinks);

          // Restore manually added platforms (platforms with URLs that weren't detected)
          const detectedPlatforms = new Set(detected.map(d => d.platform));
          const manualPlatforms = new Set<keyof SocialMediaLinks>();
          for (const [platform, url] of Object.entries(savedLinks) as [keyof SocialMediaLinks, string | null][]) {
            if (url && !detectedPlatforms.has(platform)) {
              manualPlatforms.add(platform);
            }
          }
          setManuallyAddedPlatforms(manualPlatforms);

          // Restore social text mappings if saved
          if (editingTemplate.configuration.socialTextMappings) {
            setSocialTextMappings(editingTemplate.configuration.socialTextMappings);
          }
        } else {
          setTemplateSocialLinks(merged);
          setManuallyAddedPlatforms(new Set());
          setSocialTextMappings({
            facebook_url: null,
            instagram_url: null,
            twitter_url: null,
            linkedin_url: null,
            youtube_url: null,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get selected text block ID for a mapping type
  const getSelectedTextBlock = (type: TextBlockMappingType): string | null => {
    const entry = Object.entries(textMappings).find(([_, mappingType]) => mappingType === type);
    return entry ? entry[0] : null;
  };

  // Send highlight updates to iframe
  const sendHighlightUpdate = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    // Collect all selected text IDs (from regular mappings + social mappings)
    const selectedTextIds = [
      ...Object.entries(textMappings)
        .filter(([_, type]) => type !== 'none')
        .map(([id]) => id),
      ...Object.values(socialTextMappings).filter((id): id is string => id !== null)
    ];

    // Check if selection mode is for text (including social platforms)
    const isTextSelectionMode = selectionMode === 'main_message' ||
                                 selectionMode === 'header_text' ||
                                 selectionMode === 'unsubscribe' ||
                                 selectionMode.startsWith('social_');

    iframe.contentWindow.postMessage({
      type: 'updateHighlights',
      selectionMode: isTextSelectionMode ? 'text' :
                     selectionMode === 'button' ? 'button' : 'none',
      selectedTextIds,
      hoveredElementId
    }, '*');
  }, [selectionMode, textMappings, socialTextMappings, hoveredElementId]);

  // Send highlight updates when selection mode or mappings change
  useEffect(() => {
    // Small delay to ensure iframe is ready
    const timer = setTimeout(sendHighlightUpdate, 100);
    return () => clearTimeout(timer);
  }, [sendHighlightUpdate]);

  // Also send update when iframe loads
  const handleIframeLoad = useCallback(() => {
    sendHighlightUpdate();
  }, [sendHighlightUpdate]);

  // Listen for click messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'canvaElementClick') {
        const { elementType, elementId } = event.data;

        if (elementType === 'text' && (selectionMode === 'main_message' || selectionMode === 'header_text' || selectionMode === 'unsubscribe')) {
          // Set the text mapping
          setTextMappings(prev => {
            const updated = { ...prev };
            // Clear any other block with this type
            Object.keys(updated).forEach(id => {
              if (updated[id] === selectionMode) {
                updated[id] = 'none';
              }
            });
            updated[elementId] = selectionMode;
            return updated;
          });
          setSelectionMode('none');
        }

        // Handle social platform text selection
        if (elementType === 'text' && selectionMode.startsWith('social_')) {
          const platform = SOCIAL_SELECTION_MAP[selectionMode];
          if (platform) {
            setSocialTextMappings(prev => ({
              ...prev,
              [platform]: elementId
            }));
            // Also add to manually added platforms if not already detected
            if (!detectedSocialLinks.some(d => d.platform === platform)) {
              setManuallyAddedPlatforms(prev => new Set([...prev, platform]));
            }
            setSelectionMode('none');
          }
        }

        if (elementType === 'button') {
          // Highlight the button card and scroll to it
          setHighlightedButtonId(elementId);
          const cardEl = document.getElementById(`button-card-${elementId}`);
          if (cardEl) {
            cardEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          // Clear highlight after a moment
          setTimeout(() => setHighlightedButtonId(null), 2000);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [selectionMode]);

  // Clear a text mapping
  const clearTextMapping = (type: TextBlockMappingType) => {
    setTextMappings(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(id => {
        if (updated[id] === type) {
          updated[id] = 'none';
        }
      });
      return updated;
    });
  };

  // Handle button mapping change
  const handleButtonMappingChange = (
    buttonId: string,
    type: ButtonMappingType,
    targetId?: string,
    customUrl?: string
  ) => {
    setButtonMappings(prev => ({
      ...prev,
      [buttonId]: { type, targetId, customUrl }
    }));
  };

  // Save template directly to database
  const handleSave = async () => {
    if (!user?.id) {
      toast.error('You must be logged in to save templates');
      return;
    }

    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    setSaving(true);

    try {
      // Build mappings
      const mappings: CanvaTemplateMappings = {
        textBlocks: Object.entries(textMappings).map(([blockId, mappingType]) => ({
          blockId,
          mappingType
        })),
        buttons: Object.entries(buttonMappings).map(([buttonId, mapping]) => ({
          buttonId,
          mappingType: mapping.type,
          targetId: mapping.targetId,
          customUrl: mapping.customUrl
        }))
      };

      // Generate the template (keeps original text, stores metadata)
      const generated = generateCanvaTemplate(
        htmlPreview,
        textBlocks,
        buttons,
        mappings,
        templateSocialLinks,
        socialTextMappings
      );

      // Preserve the original importSource when editing
      const importSource = editingTemplate?.configuration?.importSource || 'canva';

      // Save to database (update if editing, insert if new)
      let error;
      if (editingTemplate) {
        const result = await supabase
          .from('email_templates')
          .update({
            name: templateName.trim(),
            configuration: {
              html: generated.html,
              importSource: importSource,
              mainMessage: generated.mainMessage,
              headerText: generated.headerText,
              canvaBlocks: generated.canvaBlocks,
              socialLinks: generated.socialLinks,
              socialTextMappings: socialTextMappings,
            },
          })
          .eq('id', editingTemplate.id);
        error = result.error;
      } else {
        const result = await supabase
          .from('email_templates')
          .insert({
            name: templateName.trim(),
            configuration: {
              html: generated.html,
              importSource: 'canva',
              mainMessage: generated.mainMessage,
              headerText: generated.headerText,
              canvaBlocks: generated.canvaBlocks,
              socialLinks: generated.socialLinks,
              socialTextMappings: socialTextMappings,
            },
            user_id: user.id,
          });
        error = result.error;
      }

      if (error) throw error;

      toast.success(editingTemplate ? 'Template updated successfully' : 'Template saved successfully');
      onSaved();

    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const hasMainMessage = Object.values(textMappings).includes('main_message');
  const mainMessageBlockId = getSelectedTextBlock('main_message');
  const headerTextBlockId = getSelectedTextBlock('header_text');
  const unsubscribeBlockId = getSelectedTextBlock('unsubscribe');

  const getTextPreview = (blockId: string | null): string => {
    if (!blockId) return '';
    const block = textBlocks.find(b => b.id === blockId);
    return block?.preview || '';
  };

  // Main content - shared between dialog and inline modes
  const content = (
    <>
      <div className={inline ? "flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[80vh]" : "flex-1 overflow-hidden grid grid-cols-5 gap-6"}>
          {/* Left Panel - Controls */}
          <div className={inline ? "flex flex-col overflow-hidden" : "col-span-2 flex flex-col overflow-hidden"}>
            <Label className="text-sm font-medium mb-3">Template Settings</Label>
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-4 pr-2">
                {/* Text Mappings Section */}
                <Card>
                  <CardContent className="pt-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <Type className="h-4 w-4" />
                      Text Elements
                    </div>

                    {/* Main Message */}
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Main Message</Label>
                      {mainMessageBlockId ? (
                        <div
                          className="flex items-center gap-2 p-2 rounded-md border border-green-500/50 bg-green-500/10 cursor-pointer transition-all hover:bg-green-500/20"
                          onMouseEnter={() => setHoveredElementId(mainMessageBlockId)}
                          onMouseLeave={() => setHoveredElementId(null)}
                        >
                          <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                          <span className="text-sm truncate flex-1" title={getTextPreview(mainMessageBlockId)}>
                            {getTextPreview(mainMessageBlockId)}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => clearTextMapping('main_message')}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant={selectionMode === 'main_message' ? 'default' : 'outline'}
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => setSelectionMode(selectionMode === 'main_message' ? 'none' : 'main_message')}
                        >
                          {selectionMode === 'main_message' ? (
                            <>
                              <SquareMousePointer className="h-4 w-4 mr-2" />
                              Click text in preview...
                            </>
                          ) : (
                            <>
                              <MousePointer className="h-4 w-4 mr-2" />
                              Select Main Message
                            </>
                          )}
                        </Button>
                      )}
                    </div>

                    {/* Header Text */}
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Header Text (H1)</Label>
                      {headerTextBlockId ? (
                        <div
                          className="flex items-center gap-2 p-2 rounded-md border border-green-500/50 bg-green-500/10 cursor-pointer transition-all hover:bg-green-500/20"
                          onMouseEnter={() => setHoveredElementId(headerTextBlockId)}
                          onMouseLeave={() => setHoveredElementId(null)}
                        >
                          <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                          <span className="text-sm truncate flex-1" title={getTextPreview(headerTextBlockId)}>
                            {getTextPreview(headerTextBlockId)}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => clearTextMapping('header_text')}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant={selectionMode === 'header_text' ? 'default' : 'outline'}
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => setSelectionMode(selectionMode === 'header_text' ? 'none' : 'header_text')}
                        >
                          {selectionMode === 'header_text' ? (
                            <>
                              <SquareMousePointer className="h-4 w-4 mr-2" />
                              Click text in preview...
                            </>
                          ) : (
                            <>
                              <MousePointer className="h-4 w-4 mr-2" />
                              Select Header Text
                            </>
                          )}
                        </Button>
                      )}
                    </div>

                    {/* Unsubscribe Link */}
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Unsubscribe Link</Label>
                      {unsubscribeBlockId ? (
                        <div
                          className="flex items-center gap-2 p-2 rounded-md border border-green-500/50 bg-green-500/10 cursor-pointer transition-all hover:bg-green-500/20"
                          onMouseEnter={() => setHoveredElementId(unsubscribeBlockId)}
                          onMouseLeave={() => setHoveredElementId(null)}
                        >
                          <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                          <span className="text-sm truncate flex-1" title={getTextPreview(unsubscribeBlockId)}>
                            {getTextPreview(unsubscribeBlockId)}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => clearTextMapping('unsubscribe')}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant={selectionMode === 'unsubscribe' ? 'default' : 'outline'}
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => setSelectionMode(selectionMode === 'unsubscribe' ? 'none' : 'unsubscribe')}
                        >
                          {selectionMode === 'unsubscribe' ? (
                            <>
                              <SquareMousePointer className="h-4 w-4 mr-2" />
                              Click text in preview...
                            </>
                          ) : (
                            <>
                              <MousePointer className="h-4 w-4 mr-2" />
                              Select Unsubscribe Link
                            </>
                          )}
                        </Button>
                      )}
                    </div>

                    {!hasMainMessage && textBlocks.length > 0 && (
                      <div className="flex items-start gap-2 text-amber-600 text-xs mt-2">
                        <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span>Select a main message to make it editable when sending.</span>
                      </div>
                    )}

                    {textBlocks.length === 0 && (
                      <p className="text-xs text-muted-foreground">No text blocks found.</p>
                    )}
                  </CardContent>
                </Card>

                {/* Buttons Section */}
                <Card>
                  <CardContent className="pt-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <Link className="h-4 w-4" />
                      Buttons ({buttons.length})
                    </div>

                    {buttons.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No buttons found.</p>
                    ) : (
                      buttons.map(button => (
                        <div
                          key={button.id}
                          id={`button-card-${button.id}`}
                          className={`space-y-2 p-2 rounded-md border transition-all cursor-pointer ${
                            highlightedButtonId === button.id
                              ? 'border-primary bg-primary/10 ring-2 ring-primary'
                              : 'bg-muted/30 hover:border-primary/50'
                          }`}
                          onMouseEnter={() => setHoveredElementId(button.id)}
                          onMouseLeave={() => setHoveredElementId(null)}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="px-2 py-1 rounded text-xs font-medium truncate max-w-[150px]"
                              style={{
                                backgroundColor: button.style.backgroundColor || '#1976d2',
                                color: button.style.textColor || '#ffffff'
                              }}
                              title={button.text}
                            >
                              {button.text}
                            </div>
                          </div>

                          <Select
                            value={buttonMappings[button.id]?.type || 'keep_original'}
                            onValueChange={(value) =>
                              handleButtonMappingChange(button.id, value as ButtonMappingType)
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="keep_original">Keep original</SelectItem>
                              <SelectItem value="review">Review Form</SelectItem>
                              <SelectItem value="coupon">Coupon Offer</SelectItem>
                              <SelectItem value="loyalty">Loyalty Program</SelectItem>
                              <SelectItem value="custom">Custom URL</SelectItem>
                            </SelectContent>
                          </Select>

                          {buttonMappings[button.id]?.type === 'review' && (
                            <Select
                              value={buttonMappings[button.id]?.targetId || ''}
                              onValueChange={(value) =>
                                handleButtonMappingChange(button.id, 'review', value)
                              }
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder="Select form" />
                              </SelectTrigger>
                              <SelectContent>
                                {reviewForms.length === 0 ? (
                                  <SelectItem value="" disabled>No forms</SelectItem>
                                ) : (
                                  reviewForms.map(form => (
                                    <SelectItem key={form.id} value={form.id}>
                                      {form.title}
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          )}

                          {buttonMappings[button.id]?.type === 'coupon' && (
                            <Select
                              value={buttonMappings[button.id]?.targetId || ''}
                              onValueChange={(value) =>
                                handleButtonMappingChange(button.id, 'coupon', value)
                              }
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder="Select offer" />
                              </SelectTrigger>
                              <SelectContent>
                                {couponOffers.length === 0 ? (
                                  <SelectItem value="" disabled>No offers</SelectItem>
                                ) : (
                                  couponOffers.map(offer => (
                                    <SelectItem key={offer.id} value={offer.id}>
                                      {offer.name}
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          )}

                          {buttonMappings[button.id]?.type === 'loyalty' && (
                            <Select
                              value={buttonMappings[button.id]?.targetId || ''}
                              onValueChange={(value) =>
                                handleButtonMappingChange(button.id, 'loyalty', value)
                              }
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder="Select program" />
                              </SelectTrigger>
                              <SelectContent>
                                {loyaltyPrograms.length === 0 ? (
                                  <SelectItem value="" disabled>No programs</SelectItem>
                                ) : (
                                  loyaltyPrograms.map(program => (
                                    <SelectItem key={program.id} value={program.template_code || program.id}>
                                      {program.name}
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          )}

                          {buttonMappings[button.id]?.type === 'custom' && (
                            <Input
                              placeholder="https://..."
                              className="h-8 text-xs"
                              value={buttonMappings[button.id]?.customUrl || ''}
                              onChange={(e) =>
                                handleButtonMappingChange(button.id, 'custom', undefined, e.target.value)
                              }
                            />
                          )}
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>

                {/* Social Links Section - Always visible for manual mapping */}
                <Card>
                  <CardContent className="pt-4 space-y-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Share2 className="h-4 w-4" />
                        Social Media Links
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => {
                            // Re-run social link detection
                            const detected = detectSocialLinks(htmlPreview);
                            setDetectedSocialLinks(detected);
                            // Merge with business profile
                            const merged = mergeWithBusinessLinks(detected, businessSocialLinks);
                            setTemplateSocialLinks(merged);
                            toast.success(`Found ${detected.length} social link${detected.length !== 1 ? 's' : ''}`);
                          }}
                          title="Re-detect social links in template"
                        >
                          <Scan className="h-3 w-3 mr-1" />
                          Detect
                        </Button>
                        {(detectedSocialLinks.length > 0 || manuallyAddedPlatforms.size > 0) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => {
                              setTemplateSocialLinks(businessSocialLinks);
                              setManuallyAddedPlatforms(new Set());
                              setSocialTextMappings({
                                facebook_url: null,
                                instagram_url: null,
                                twitter_url: null,
                                linkedin_url: null,
                                youtube_url: null,
                              });
                            }}
                            title="Reset all social links"
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Reset
                          </Button>
                        )}
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground mb-3">
                      {detectedSocialLinks.length > 0
                        ? `Detected ${detectedSocialLinks.length} social link${detectedSocialLinks.length > 1 ? 's' : ''}. Click "Select text" to manually map additional platforms.`
                        : 'Click "Select text" to map text in the preview to social links.'}
                    </p>

                    <div className="space-y-3">
                      {/* Render all active platforms (detected + manually added) */}
                      {(() => {
                        const detectedPlatforms = new Set(detectedSocialLinks.map(d => d.platform));
                        const activePlatforms = new Set([...detectedPlatforms, ...manuallyAddedPlatforms]);

                        return Array.from(activePlatforms).map((platform) => {
                          const Icon = platform === 'facebook_url' ? Facebook :
                                       platform === 'instagram_url' ? Instagram :
                                       platform === 'twitter_url' ? Twitter :
                                       platform === 'linkedin_url' ? Linkedin :
                                       platform === 'youtube_url' ? Youtube : Share2;
                          const detectedLink = detectedSocialLinks.find(d => d.platform === platform);
                          const isDetected = !!detectedLink;
                          const mappedBlockId = socialTextMappings[platform];
                          const mappedBlock = mappedBlockId ? textBlocks.find(b => b.id === mappedBlockId) : null;
                          const selectionModeKey = `social_${platform.replace('_url', '')}` as SelectionMode;
                          const isSelecting = selectionMode === selectionModeKey;

                          return (
                            <div key={platform} className="space-y-1.5 p-2 rounded-md border bg-muted/30">
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <span className="text-xs font-medium flex-1">{SOCIAL_PLATFORM_NAMES[platform]}</span>
                                {isDetected && (
                                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                    Auto
                                  </Badge>
                                )}
                                {!isDetected && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0"
                                    onClick={() => {
                                      setManuallyAddedPlatforms(prev => {
                                        const next = new Set(prev);
                                        next.delete(platform);
                                        return next;
                                      });
                                      setTemplateSocialLinks(prev => ({
                                        ...prev,
                                        [platform]: null
                                      }));
                                      setSocialTextMappings(prev => ({
                                        ...prev,
                                        [platform]: null
                                      }));
                                    }}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>

                              {/* Text mapping - click to select */}
                              <div className="space-y-1">
                                <Label className="text-[10px] text-muted-foreground">Text element</Label>
                                {mappedBlock ? (
                                  <div
                                    className="flex items-center gap-2 p-1.5 rounded border border-green-500/50 bg-green-500/10 cursor-pointer transition-all hover:bg-green-500/20"
                                    onMouseEnter={() => setHoveredElementId(mappedBlockId)}
                                    onMouseLeave={() => setHoveredElementId(null)}
                                  >
                                    <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                                    <span className="text-xs truncate flex-1" title={mappedBlock.preview}>
                                      {mappedBlock.preview}
                                    </span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-5 w-5 p-0"
                                      onClick={() => setSocialTextMappings(prev => ({
                                        ...prev,
                                        [platform]: null
                                      }))}
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : isDetected && detectedLink ? (
                                  (() => {
                                    // Try to find the text block that contains the detected text for hover highlighting
                                    const detectedText = detectedLink.detectedText || SOCIAL_PLATFORM_NAMES[platform];
                                    const matchingBlock = textBlocks.find(b =>
                                      b.content.toLowerCase().includes(detectedText.toLowerCase())
                                    );
                                    return (
                                      <div
                                        className="flex items-center gap-2 p-1.5 rounded border border-green-500/50 bg-green-500/10 cursor-pointer transition-all hover:bg-green-500/20"
                                        onMouseEnter={() => matchingBlock && setHoveredElementId(matchingBlock.id)}
                                        onMouseLeave={() => setHoveredElementId(null)}
                                      >
                                        <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <span className="text-xs font-medium text-green-700 dark:text-green-300 truncate block" title={detectedLink.detectedText}>
                                            "{detectedLink.detectedText || SOCIAL_PLATFORM_NAMES[platform]}"
                                          </span>
                                          <span className="text-[10px] text-muted-foreground">
                                            {detectedLink.isTextMention ? 'Text found' : 'Link found'}
                                          </span>
                                        </div>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 text-[10px] px-2 flex-shrink-0"
                                          onClick={() => setSelectionMode(isSelecting ? 'none' : selectionModeKey)}
                                        >
                                          {isSelecting ? 'Cancel' : 'Override'}
                                        </Button>
                                      </div>
                                    );
                                  })()
                                ) : (
                                  <Button
                                    variant={isSelecting ? 'default' : 'outline'}
                                    size="sm"
                                    className="w-full h-7 justify-start text-xs"
                                    onClick={() => setSelectionMode(isSelecting ? 'none' : selectionModeKey)}
                                  >
                                    {isSelecting ? (
                                      <>
                                        <SquareMousePointer className="h-3 w-3 mr-1.5" />
                                        Click text in preview...
                                      </>
                                    ) : (
                                      <>
                                        <MousePointer className="h-3 w-3 mr-1.5" />
                                        Select text
                                      </>
                                    )}
                                  </Button>
                                )}
                              </div>

                              {/* URL input */}
                              <div className="space-y-1">
                                <Label className="text-[10px] text-muted-foreground">Link URL</Label>
                                <Input
                                  placeholder={`https://${platform.replace('_url', '')}.com/...`}
                                  className="h-7 text-xs"
                                  value={templateSocialLinks[platform] || ''}
                                  onChange={(e) => setTemplateSocialLinks(prev => ({
                                    ...prev,
                                    [platform]: e.target.value || null
                                  }))}
                                />
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>

                    {/* Add platform dropdown */}
                    {(() => {
                      const detectedPlatforms = new Set(detectedSocialLinks.map(d => d.platform));
                      const availablePlatforms = (Object.keys(SOCIAL_PLATFORM_NAMES) as (keyof SocialMediaLinks)[])
                        .filter(p => !detectedPlatforms.has(p) && !manuallyAddedPlatforms.has(p));

                      if (availablePlatforms.length === 0) return null;

                      return (
                        <Select
                          value=""
                          onValueChange={(value) => {
                            const platform = value as keyof SocialMediaLinks;
                            setManuallyAddedPlatforms(prev => new Set([...prev, platform]));
                            // Pre-fill URL from business profile if available
                            if (businessSocialLinks[platform]) {
                              setTemplateSocialLinks(prev => ({
                                ...prev,
                                [platform]: businessSocialLinks[platform]
                              }));
                            }
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Plus className="h-3 w-3" />
                              <span>Add social platform...</span>
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            {availablePlatforms.map(platform => (
                              <SelectItem key={platform} value={platform}>
                                {SOCIAL_PLATFORM_NAMES[platform]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    })()}

                    {Object.values(businessSocialLinks).some(v => v) && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Tip: Update your business profile to set default social URLs.
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </ScrollArea>
          </div>

          {/* Right Panel - Preview */}
          <div className={inline ? "flex flex-col overflow-hidden" : "col-span-3 flex flex-col overflow-hidden"}>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-medium">Email Preview</Label>
              {selectionMode !== 'none' && (
                <Badge variant="secondary" className="animate-pulse">
                  <SquareMousePointer className="h-3 w-3 mr-1" />
                  {selectionMode === 'main_message' && 'Click on main message text'}
                  {selectionMode === 'header_text' && 'Click on header text'}
                  {selectionMode === 'unsubscribe' && 'Click on unsubscribe text'}
                  {selectionMode === 'button' && 'Click on a button'}
                  {selectionMode === 'social_facebook' && 'Click on Facebook text'}
                  {selectionMode === 'social_instagram' && 'Click on Instagram text'}
                  {selectionMode === 'social_twitter' && 'Click on Twitter/X text'}
                  {selectionMode === 'social_linkedin' && 'Click on LinkedIn text'}
                  {selectionMode === 'social_youtube' && 'Click on YouTube text'}
                </Badge>
              )}
            </div>
            <div
              className={`flex-1 border rounded-lg overflow-hidden bg-white transition-all ${
                selectionMode !== 'none' ? 'ring-2 ring-primary' : ''
              }`}
            >
              <iframe
                ref={iframeRef}
                srcDoc={interactiveHtml}
                title="Email Preview"
                className="w-full h-full"
                style={{ minHeight: '500px', border: 'none' }}
                onLoad={handleIframeLoad}
              />
            </div>
          </div>
        </div>

        <Separator className="my-2" />

        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Label htmlFor="template-name" className="text-xs text-muted-foreground mb-1 block">
              Template Name
            </Label>
            <Input
              id="template-name"
              placeholder="Enter template name..."
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="flex gap-3 pt-5">
            <Button variant="outline" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading || saving || !templateName.trim()}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Template
                </>
              )}
            </Button>
          </div>
        </div>
      </>
  );

  // Inline mode - full page layout like EmailTemplateBuilder
  if (inline) {
    return (
      <div className="p-6 space-y-6 w-full">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold">
              {editingTemplate ? 'Edit Canva Template' : 'Configure Canva Template'}
            </h2>
            <p className="text-gray-600 mt-1">
              Click on elements in the preview to map them to TagInn features.
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onCancel} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>
        {content}
      </div>
    );
  }

  // Dialog mode - popup
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{editingTemplate ? 'Edit Canva Template' : 'Configure Canva Template'}</DialogTitle>
          <DialogDescription>
            Click on elements in the preview to map them to TagInn features.
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}

export default CanvaTemplateMappingDialog;
